import type { ReactNode } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

export function WorkspacePageGrid({
  main,
  aside,
  className,
}: {
  main: ReactNode
  aside?: ReactNode
  className?: string
}) {
  return (
    <section className={cn('grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]', className)}>
      <div className="min-w-0 space-y-6">{main}</div>
      {aside ? <aside className="min-w-0 space-y-4 xl:sticky xl:top-20 xl:self-start">{aside}</aside> : null}
    </section>
  )
}

export function WorkspaceSection({
  title,
  description,
  actions,
  children,
  className,
  tone = 'default',
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
  tone?: 'default' | 'danger'
}) {
  return (
    <Card
      className={cn(
        'page-section-panel rounded-[26px]',
        tone === 'danger' && 'border-destructive/30 bg-destructive/[0.03]',
        className,
      )}
    >
      <CardHeader className="gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 flex-1 space-y-1.5">
            <CardTitle className={cn('break-words text-xl', tone === 'danger' && 'text-destructive')}>
              {title}
            </CardTitle>
            {description ? <CardDescription className="max-w-3xl break-words">{description}</CardDescription> : null}
          </div>
          {actions ? (
            <div className="flex min-w-0 flex-wrap items-center gap-3 md:justify-end [&>*]:min-w-0 [&>*]:max-w-full">
              {actions}
            </div>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">{children}</CardContent>
    </Card>
  )
}

export function WorkspaceAsideCard({
  title,
  description,
  children,
  className,
  tone = 'default',
}: {
  title?: ReactNode
  description?: ReactNode
  children?: ReactNode
  className?: string
  tone?: 'default' | 'accent' | 'danger'
}) {
  return (
    <div
      className={cn(
        'page-section-panel rounded-[24px] px-5 py-5',
        tone === 'accent' && 'border-primary/20 bg-primary/[0.08]',
        tone === 'danger' && 'border-destructive/30 bg-destructive/[0.05]',
        className,
      )}
    >
      {title ? <p className="break-words text-sm font-medium text-foreground">{title}</p> : null}
      {description ? (
        <p className={cn('break-words text-sm leading-6 text-muted-foreground', title ? 'mt-1.5' : undefined)}>
          {description}
        </p>
      ) : null}
      {children ? <div className={cn(title || description ? 'mt-4' : undefined)}>{children}</div> : null}
    </div>
  )
}
